import React, { useState, useMemo } from "react";
import { Box, Typography, Button } from "@mui/material";
import {
  ArrowBack as ArrowLeft,
  Download,
  Error as AlertCircle,
} from "@mui/icons-material";
import { UploadZone } from "./UploadZone";
import { StepIndicator } from "./StepIndicator";
import CommonTable1 from "app/components/Tables/CommonTable1";
import * as XLSX from "xlsx";

export function SchemaWorkflow({ schema, onBack, onFileValidated }) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const generateSampleData = () => {
    const sampleRows = [];

    // Create base rows with identifier fields
    if (schema.identifierFields) {
      sampleRows.push({ [schema.identifierFields[0]]: "ID001" });
      sampleRows.push({ [schema.identifierFields[0]]: "ID002" });
      sampleRows.push({ [schema.identifierFields[0]]: "ID003" });
    } else {
      sampleRows.push({});
      sampleRows.push({});
      sampleRows.push({});
    }

    schema.columns.forEach((col) => {
      // Skip identifier fields as they're already added
      if (
        schema.identifierFields &&
        schema.identifierFields.includes(col.name)
      ) {
        return;
      }

      if (schema.id === "house") {
        sampleRows[0][col.name] =
          col.name === "House Name"
            ? "Phoenix"
            : col.name === "House Color"
            ? "Red"
            : col.name === "Role"
            ? "Captain"
            : "";
        sampleRows[1][col.name] =
          col.name === "House Name"
            ? "Dragon"
            : col.name === "House Color"
            ? "Blue"
            : col.name === "Role"
            ? "Member"
            : "";
        sampleRows[2][col.name] =
          col.name === "House Name"
            ? "Griffin"
            : col.name === "House Color"
            ? "Green"
            : col.name === "Role"
            ? "Vice Captain"
            : "";
      } else if (schema.id === "parent") {
        sampleRows[0][col.name] =
          col.name === "Father Name"
            ? "Rajesh Kumar"
            : col.name === "Mother Name"
            ? "Priya Kumar"
            : col.name === "Guardian Phone"
            ? "9876543210"
            : col.name === "Guardian Email"
            ? "rajesh@example.com"
            : col.name === "Father Occupation"
            ? "Engineer"
            : "";
        sampleRows[1][col.name] =
          col.name === "Father Name"
            ? "Amit Sharma"
            : col.name === "Mother Name"
            ? "Neha Sharma"
            : col.name === "Guardian Phone"
            ? "9876543211"
            : col.name === "Guardian Email"
            ? "amit@example.com"
            : col.name === "Father Occupation"
            ? "Doctor"
            : "";
        sampleRows[2][col.name] =
          col.name === "Father Name"
            ? "Suresh Patel"
            : col.name === "Mother Name"
            ? "Anjali Patel"
            : col.name === "Guardian Phone"
            ? "9876543212"
            : col.name === "Guardian Email"
            ? "suresh@example.com"
            : col.name === "Father Occupation"
            ? "Teacher"
            : "";
      } else if (schema.id === "class") {
        sampleRows[0][col.name] =
          col.name === "Class"
            ? "10"
            : col.name === "Section"
            ? "A"
            : col.name === "Roll Number"
            ? "1"
            : "";
        sampleRows[1][col.name] =
          col.name === "Class"
            ? "10"
            : col.name === "Section"
            ? "B"
            : col.name === "Roll Number"
            ? "2"
            : "";
        sampleRows[2][col.name] =
          col.name === "Class"
            ? "9"
            : col.name === "Section"
            ? "A"
            : col.name === "Roll Number"
            ? "5"
            : "";
      } else {
        sampleRows[0][col.name] = "Sample Value";
        sampleRows[1][col.name] = "Sample Value";
        sampleRows[2][col.name] = "Sample Value";
      }
    });

    return sampleRows;
  };

  const handleDownloadTemplate = () => {
    const sampleData = generateSampleData();
    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, `${schema.name.replace(/\s+/g, "_")}_Template.xlsx`);
  };

  const handleFileUpload = (file) => {
    setUploadedFile(file);
  };

  const validateData = (data) => {
    const errors = [];
    let validCount = 0;
    let invalidCount = 0;

    data.forEach((row, index) => {
      const rowErrors = [];

      // Check identifier fields (at least one should be present)
      if (schema.identifierFields) {
        const hasIdentifier = schema.identifierFields.some(
          (field) => row[field] && row[field].toString().trim() !== ""
        );
        if (!hasIdentifier) {
          rowErrors.push(
            `At least one of ${schema.identifierFields.join(", ")} is required`
          );
        }
      }

      // Check required fields
      schema.columns.forEach((col) => {
        if (
          col.required &&
          (!row[col.name] || row[col.name].toString().trim() === "")
        ) {
          rowErrors.push(`Missing ${col.name}`);
        }
      });

      // if (schema.id === "house" && row["House Color"]) {
      //   rowErrors.push("Invalid House Color");
      // }

      if (rowErrors.length > 0) {
        invalidCount++;
        errors.push({ rowIndex: index, errors: rowErrors, data: row });
      } else {
        validCount++;
      }
    });

    return {
      total: data.length,
      valid: validCount,
      invalid: invalidCount,
      errors,
      allData: data.map((row, index) => ({
        ...row,
        _rowIndex: index,
        _hasErrors: errors.some((e) => e.rowIndex === index),
      })),
    };
  };

  const handleValidate = () => {
    if (!uploadedFile) return;

    setIsValidating(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        const validationResults = validateData(jsonData);

        setTimeout(() => {
          setIsValidating(false);
          onFileValidated(jsonData, validationResults);
        }, 1000);
      } catch (error) {
        console.error("Error parsing file:", error);
        setIsValidating(false);
      }
    };

    reader.readAsBinaryString(uploadedFile);
  };

  const sampleData = generateSampleData();

  // Prepare columns for CommonTable1
  const tableColumns = useMemo(() => {
    return schema.columns.map((col) => {
      const isIdentifierField = schema.identifierFields?.includes(col.name);
      const showRequired =
        col.required ||
        (isIdentifierField && schema.identifierFields.length === 1);

      return {
        accessorKey: col.name,
        header: col.name,
        Header: () => (
          <Box>
            {col.name}
            {showRequired && (
              <Typography component="span" sx={{ color: "#EF4444", ml: 0.5 }}>
                *
              </Typography>
            )}
            {isIdentifierField && schema.identifierFields.length > 1 && (
              <Typography
                component="span"
                sx={{
                  color: "#F59E0B",
                  ml: 0.5,
                  fontSize: "12px",
                  fontStyle: "italic",
                }}
              >
                (any one)
              </Typography>
            )}
          </Box>
        ),
        Cell: ({ cell }) => (
          <Typography sx={{ fontSize: "14px" }}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      };
    });
  }, [schema]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#F9FAFB",
        borderRadius: "10px",
      }}
    >
      <Box
        sx={{
          // maxWidth: "1152px",
          margin: "0 auto",
          padding: { xs: "24px 16px", sm: "24px 24px", lg: "24px 32px" },
        }}
      >
        <Button
          onClick={onBack}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#6B7280",
            textTransform: "none",
            marginBottom: "24px",
            "&:hover": {
              color: "#111827",
              backgroundColor: "transparent",
            },
          }}
        >
          <ArrowLeft style={{ width: 20, height: 20 }} />
          Back to Dashboard
        </Button>

        <Box sx={{ mb: 4 }}>
          <StepIndicator currentStep={1} />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
            Bulk Update: {schema.name}
          </Typography>
          <Typography sx={{ color: "#6B7280" }}>
            Upload data to update {schema.description.toLowerCase()}.
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "#FFFBEB",
            border: "1px solid #FDE68A",
            borderRadius: "8px",
            padding: "16px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            marginBottom: "32px",
          }}
        >
          <AlertCircle
            style={{
              width: 20,
              height: 20,
              color: "#92400E",
              flexShrink: 0,
              marginTop: 2,
            }}
          />
          <Typography sx={{ color: "#92400E", fontSize: "14px" }}>
            <Typography component="span" sx={{ fontWeight: 600 }}>
              Required:
            </Typography>{" "}
            {schema.identifierFields
              ? `At least one of "${schema.identifierFields.join(
                  '", "'
                )}" is required in every row.`
              : '"Admission Number" and "Session" are mandatory in every upload.'}{" "}
            Download the template below for the correct format.
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            padding: "24px",
            marginBottom: "32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ color: "#111827", mb: 0.5, fontWeight: 600 }}
              >
                Download Template
              </Typography>
              <Typography sx={{ color: "#6B7280", fontSize: "14px" }}>
                Use this template to ensure correct data format
              </Typography>
            </Box>
            <Button
              onClick={handleDownloadTemplate}
              variant="contained"
              startIcon={<Download style={{ width: 16, height: 16 }} />}
              sx={{
                px: 2,
                py: 1,
                textTransform: "none",
                backgroundColor: "#3B82F6",
                "&:hover": {
                  backgroundColor: "#2563EB",
                },
              }}
            >
              Download Sample (.xlsx)
            </Button>
          </Box>

          <CommonTable1
            columns={tableColumns}
            data={sampleData}
            maxHeight="400px"
            enableColumnActions={false}
            enableSorting={false}
          />
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            padding: "24px",
            marginBottom: "32px",
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: "#111827", mb: 2, fontWeight: 600 }}
          >
            Upload File
          </Typography>

          <UploadZone
            onFileSelect={handleFileUpload}
            acceptedFormats={[".csv", ".xls", ".xlsx"]}
          />

          {uploadedFile && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#F0FDF4",
                border: "1px solid #BBF7D0",
                borderRadius: "8px",
              }}
            >
              <Typography sx={{ color: "#15803D" }}>
                File uploaded:{" "}
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  {uploadedFile.name}
                </Typography>
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
            <Button
              onClick={handleValidate}
              disabled={!uploadedFile || isValidating}
              variant="contained"
              sx={{
                flex: 1,
                px: 3,
                py: 1.5,
                textTransform: "none",
                backgroundColor: "#3B82F6",
                "&:hover": {
                  backgroundColor: "#2563EB",
                },
                "&:disabled": {
                  backgroundColor: "#D1D5DB",
                },
              }}
            >
              {isValidating ? "Validating..." : "Validate Data"}
            </Button>
            {uploadedFile && (
              <Button
                onClick={() => setUploadedFile(null)}
                variant="outlined"
                sx={{
                  px: 3,
                  py: 1.5,
                  textTransform: "none",
                  borderColor: "#D1D5DB",
                  color: "#374151",
                  "&:hover": {
                    backgroundColor: "#F9FAFB",
                    borderColor: "#D1D5DB",
                  },
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
